import type { BaseComponentProps, NameValueObject } from "@neru/types";
import { isNotEmpty, toNameValueObject } from "@neru/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FORM_LABEL_STYLES } from "../../constants";
import { useKeyboardNavigation } from "../../hooks/keyboardNavigation";
import { getDropdownVerticalPosition } from "../../utils";
import { Button } from "../Button/Button";
import { Icon } from "../Icon/Icon";
import { type MenuItem, renderMenuItems } from "../Menu/Menu";
import { OnBlurDetector } from "../OnBlurDetector/OnBlurDetector";

type Option<T> = NameValueObject<T> | T;

export interface SelectProps<T, U> extends BaseComponentProps {
  defaultValue?: Option<T>;
  disabled?: boolean;
  emptyOption?: boolean;
  emptyOptionLabel?: string;
  emptyOptionValue?: T;
  items: Array<Option<T> | null | undefined>;
  label?: string;
  menuItems?: Array<MenuItem<U>>;
  name?: string;
  onChange?: (value: NameValueObject<T>["value"]) => void;
  onNavigate?: (value: U) => void;
  required?: boolean;
  width?: string | number;
}

export const itemStyles = `
    flex items-center cursor-pointer whitespace-nowrap select-none relative
    mx-1 hover:bg-primary-50 focus:outline-none focus:bg-primary-50 
    dark:hover:bg-primary-900 dark:focus:bg-primary-900 rounded-lg `;

export const Select = <T extends string | number, U extends string>({
  className,
  "data-testid": testId = "select",
  defaultValue,
  disabled,
  emptyOption,
  emptyOptionLabel = "All items",
  emptyOptionValue = " " as T,
  items,
  label,
  menuItems = [],
  name,
  onChange,
  onNavigate,
  required,
  width,
}: SelectProps<T, U>) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleSelect = () => setIsOpen(!isOpen);
  const groupRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { focusedIndex, handleKeyDown } = useKeyboardNavigation(
    groupRef.current,
  );

  const normalizedItems = items.filter(isNotEmpty).map(toNameValueObject);
  const options = useMemo(
    () =>
      emptyOption
        ? [
            {
              name: emptyOptionLabel,
              value: emptyOptionValue,
            },
            ...normalizedItems,
          ]
        : normalizedItems,
    [emptyOption, emptyOptionLabel, emptyOptionValue, normalizedItems],
  );

  const getPreselectedValue = useCallback(() => {
    if (defaultValue) {
      return toNameValueObject(defaultValue).value;
    }

    return options[0]?.value;
  }, [defaultValue, options]);

  const [selectedOption, setSelectedOption] = useState(getPreselectedValue());

  const onSelectedHandler = (option: T) => {
    setSelectedOption(option);
    setIsOpen(false);

    if (onChange) {
      onChange(option);
    }
  };

  const onMenuHandler = (value: U) => {
    if (onNavigate) {
      onNavigate(value);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (defaultValue) {
      setSelectedOption(toNameValueObject(defaultValue).value);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const container = containerRef.current;
    const dropdown = groupRef.current;
    if (!container || !dropdown) {
      return;
    }

    const setPosition = () => {
      const verticalExpandTo = getDropdownVerticalPosition(dropdown, {
        position: "bottom",
      });

      const dropdownHeight = dropdown.clientHeight;
      const containerRect = container.getBoundingClientRect();

      const top =
        verticalExpandTo === "top" ? -dropdownHeight : containerRect.height;

      const positionStyles = {
        marginTop: verticalExpandTo === "top" ? 0 : "4px",
        top: `${top}px`,
        transformOrigin: `${verticalExpandTo === "top" ? "bottom" : "top"}`,
      };

      dropdown.dataset["positioned"] = "true";
      Object.assign(dropdown.style, positionStyles);
    };

    const resizeObserver = new ResizeObserver(() => {
      setPosition();
    });

    resizeObserver.observe(dropdown);

    return () => {
      if (dropdown) {
        resizeObserver.unobserve(dropdown);
      }
    };
  }, [isOpen]);

  const borderStyles = className?.includes("border-")
    ? ""
    : "border-primary-950 dark:border-primary-300";
  const buttonStyles = `justify-start w-full truncate relative overflow-x-hidden
   pl-4 pr-10 h-10 mt-0.5 bg-primary-0 dark:bg-primary-1000 dark:text-primary-0 
   text-left ${borderStyles} border hover:bg-primary-0 hover:border-primary-950 
   dark:hover:border-primary-50 dark:hover:bg-primary-1000 rounded-lg 
   focus:outline-none active:bg-primary-0 dark:active:bg-primary-1000
  ${disabled && "disabled:border-primary-300 disabled:bg-primary-100 disabled:text-primary-300 dark:disabled:bg-primary-900 dark:disabled:border-primary-600 dark:disabled:text-primary-600"}
  ${isOpen ? "focus:ring-1 focus:ring-primary-950 focus:border-primary-950 dark:focus:ring-primary-50 dark:focus:border-primary-50" : ""}`;
  const chevronIconStyles = `absolute top-1/2 right-3 transform -translate-y-1/2 transition-transform ${isOpen ? "rotate-180" : ""}`;
  const getOptionStyles = (value: T) =>
    selectedOption === value
      ? "bg-primary-100 dark:bg-primary-700 hover:bg-primary-200 dark:hover:bg-primary-700 focus:bg-primary-100 dark:focus:bg-primary-700"
      : "";

  const renderMenu = () =>
    menuItems.length ? (
      <>
        {renderMenuItems({
          focusedIndex,
          handleKeyDown,
          handler: onMenuHandler,
          items: menuItems,
          testId,
        })}{" "}
        <div className="border-primary-200 mx-4 my-2 border-b" />
      </>
    ) : null;

  const renderOptionItems = () => (
    <ul className="max-h-96 overflow-y-scroll" role="listbox">
      {options.map(({ name, value }, index) => {
        const handler = () => onSelectedHandler(value);

        return (
          <li
            aria-selected={selectedOption === value}
            className={[itemStyles, getOptionStyles(value)].join(" ")}
            key={value}
            onKeyDown={(e) => handleKeyDown(e, handler)}
            ref={(el) => {
              if (focusedIndex === menuItems.length + index) {
                el?.focus();
              }
            }}
            role="option"
            tabIndex={0}
          >
            <button
              className={`
                font-inherit h-full w-full border-0 bg-transparent p-3 text-left
              `}
              data-testid={`${testId}-${value === " " ? "all" : value}`}
              onClick={handler}
              type="button"
            >
              {name}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <OnBlurDetector onBlur={() => setIsOpen(false)}>
      <div
        className="relative mx-px inline-block w-auto"
        data-testid={testId}
        ref={containerRef}
        style={{ width: width ?? "auto" }}
      >
        {label ? (
          <label className={FORM_LABEL_STYLES} data-testid={`${testId}-label`}>
            {label} {required && <span className="sr-only">*</span>}
          </label>
        ) : null}
        <Button
          className={[buttonStyles, className].join(" ")}
          data-testid={`${testId}-button`}
          disabled={disabled}
          name={name}
          onClick={toggleSelect}
          onKeyDown={(e) => handleKeyDown(e, toggleSelect)}
          type="button"
        >
          <span className="mr-1 grow truncate">
            {options?.find(({ value }) => value === selectedOption)?.name ??
              "-"}
          </span>
          <Icon
            className={chevronIconStyles}
            size="small"
            type="chevron-down-line"
          />
        </Button>

        <div
          className={`
            bg-primary-0 absolute z-10 rounded-md py-2 shadow-lg drop-shadow-xl
            dark:bg-primary-800 dark:text-primary-0
          `}
          data-testid={`${testId}-group`}
          ref={groupRef}
          style={{ display: isOpen ? "block" : "none" }}
        >
          {renderMenu()}
          {options.length ? renderOptionItems() : null}
        </div>
      </div>
    </OnBlurDetector>
  );
};
